<template>
  <div class="page-content-wrapper client-admins-container">
    <!-- Data Hub Banner -->
    <div class="card shadow-sm border rounded-3 mb-4 data-hub-card">
      <div class="data-hub-banner p-3 d-flex align-items-center justify-content-between">
        <div class="d-flex align-items-center gap-3">
          <div class="hub-icon d-flex align-items-center justify-content-center">
            <!-- simple data icon -->
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="3" width="7" height="7" rx="1" fill="currentColor" opacity="0.9"/>
              <rect x="14" y="3" width="7" height="7" rx="1" fill="currentColor" opacity="0.9"/>
              <rect x="3" y="14" width="7" height="7" rx="1" fill="currentColor" opacity="0.9"/>
              <rect x="14" y="14" width="7" height="7" rx="1" fill="currentColor" opacity="0.9"/>
            </svg>
          </div>
          <div>
            <h2 class="mb-0 fs-5 banner-heading">
              <span class="banner-title">Data Hub</span>
              <span class="banner-sep"> — </span>
              <span class="banner-subtitle">Client Admins Overview</span>
            </h2>
            <p class="banner-desc mb-0">
              Select a client admin to view their data profiles and customer applications.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Search Bar (CustomTable style) -->
    <div class="d-flex justify-content-end align-items-center mb-3" style="max-width:100%;">
      <label class="fs-6 text-muted mt-3 fw-bold me-auto">Client Admins</label>
      <span class="input-group-text border-3 bg-transparent me-2" id="search-addon">
        <i class="fas fa-search text-muted"></i>
      </span>
      <input
        v-model="search"
        type="text"
        class="form-control rounded-3 w-25"
        placeholder="Search by name, company, or reg number..."
        @input="onSearch"
      />
    </div>

    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status"></div>
    </div>
    <div v-else-if="error" class="alert alert-danger">{{ error }}</div>
    <div v-else>
      <div class="row g-3">
        <div
          v-for="admin in filteredAdmins"
          :key="admin.id"
          class="col-md-4 col-sm-6"
        >
          <div class="card shadow-sm border rounded-3 admin-card" @click="selectAdmin(admin)">
            <div class="card-body d-flex align-items-center">
              <div class="avatar-block me-3">
                <i class="bi bi-person-badge fs-3 text-primary"></i>
              </div>
              <div class="flex-grow-1">
                <div class="fw-bold fs-6">{{ admin.full_name }}</div>
                <div class="text-muted mb-1">{{ admin.company_name }}</div>
                <div class="text-muted mb-1">{{ admin.company_reg_no }}</div>
                <div class="d-flex gap-2">
                  <span class="badge-soft badge-soft-secondary">
                    <i class="bi bi-people me-1"></i>
                    {{ admin.entity_count }} profiles
                  </span>
                </div>
              </div>
              <div>
                <button class="btn btn-bg-secondary btn-sm rounded-3" @click.stop="selectAdmin(admin)">
                  View
                </button>
              </div>
            </div>
          </div>
        </div>
        <div v-if="filteredAdmins.length === 0" class="col-12 text-center text-muted py-5">
          No client admins found.
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { API_BASE_URL } from "@/config";

export default {
  name: "ClientAdmins",
  data() {
    return {
      clientAdmins: [],
      loading: false,
      error: null,
      search: "",
      selectedClientAdminId: null,
    };
  },
  computed: {
    filteredAdmins() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.clientAdmins;
      return this.clientAdmins.filter(admin => {
        return (
          admin.full_name.toLowerCase().includes(q) ||
          admin.company_name.toLowerCase().includes(q) ||
          admin.company_reg_no.toLowerCase().includes(q)
        );
      });
    },
  },
  methods: {
    async fetchClientAdmins() {
      this.loading = true;
      this.error = null;
      try {
        const TOKEN = localStorage.getItem("token");
        if (!TOKEN) throw new Error("Missing auth token");
        const url = `${API_BASE_URL}/verify_api/data_hub/client_admins/`;
        const resp = await fetch(url, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${TOKEN}`,
          },
        });
        if (!resp.ok) throw new Error(`Failed to fetch client admins (${resp.status})`);
        const data = await resp.json();
        this.clientAdmins = Array.isArray(data) ? data.map(admin => ({
          id: admin.id,
          full_name: `${admin.first_name} ${admin.last_name}`,
          company_name: admin.company_name || "-",
          company_reg_no: admin.company_reg_no || "-",
          entity_count: admin.entity_count ?? 0,
          raw: admin,
        })) : [];
      } catch (err) {
        this.error = err.message || String(err);
      } finally {
        this.loading = false;
      }
    },
    selectAdmin(admin) {
      this.$router.push({ name: 'data_hub', params: { clientAdminId: admin.id } });
    },
  },
  mounted() {
    this.fetchClientAdmins();
  },
};
</script>

<style scoped>
.client-admins-container {
  min-height: 300px;
}
.admin-card {
  cursor: pointer;
  transition: box-shadow 0.2s;
}
.admin-card:hover {
  box-shadow: 0 8px 24px rgba(16,24,40,0.12);
  border-color: var(--primary-color, #0ea5a9);
}
.avatar-block {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background: rgba(13,110,253,0.08);
  display: flex;
  align-items: center;
  justify-content: center;
}
.badge-soft {
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
}
.badge-soft-secondary {
  background: rgba(108,117,125,0.1);
  color: #6c757d;
}
.badge-soft-success {
  background: rgba(25,135,84,0.1);
  color: var(--bs-success);
}
.data-hub-card {
  overflow: hidden;
}
.data-hub-banner {
  background: #fff;
  color: #212529;
  box-shadow: 0 8px 20px rgba(16, 24, 40, 0.06);
}
.hub-icon {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background: #f1f3f5;
  border: 1px solid rgba(0, 0, 0, 0.04);
}
.banner-heading {
  display: flex;
  align-items: baseline;
  gap: 6px;
}
.banner-title {
  font-weight: 700;
  color: #111827;
}
.banner-sep {
  color: rgba(0, 0, 0, 0.45);
}
.banner-subtitle {
  font-weight: 600;
  font-size: 0.9rem;
  color: rgba(0, 0, 0, 0.65);
}
.banner-desc {
  font-size: 0.75rem;
  line-height: 1.2;
  color: rgba(0, 0, 0, 0.55);
  margin-top: 2px;
}
.input-group-text {
  border-radius: 0.375rem 0 0 0.375rem;
  background: transparent;
  border: none;
  padding-right: 0;
}
.form-control.w-25 {
  min-width: 220px;
  max-width: 320px;
}
</style>